import {
  identityOperator,
  type BooleanComparator,
  type DateComparator,
  type EnumComparator,
  type Filter,
  type FilterComparator,
  type FilterDataType,
  type IdentityComparator,
  type NumericComparator,
  type SetComparator,
  type StringComparator,
} from "./FilterSchema";
import { anyIdentityTypeLabel, IDENTITY_TYPE_LABELS } from "./identityTypes";

/**
 * UI labels and a11y phrasing for the OperatorPicker pills.
 *
 * Tuple / type / predicate ownership lives in `FilterSchema`; this
 * module is the presentation layer. Keeping the map structurally
 * exhaustive (no `Partial<>`) means adding a new comparator to any
 * per-type tuple in `FilterSchema` fails to compile here until a label
 * is supplied.
 *
 * The same wire comparator can read differently depending on the field
 * type — `EQUALS` is "equals" for strings, "=" for numbers, "is" for
 * enums — so labels are keyed on `(FilterDataType, comparator)`.
 */
// Numeric glyph labels, shared by the `numeric` and `metric` types (a metric
// renders identically to a numeric filter — only `Filter["type"]` differs).
const numericLabels: Record<NumericComparator, string> = {
  EQUALS: "=",
  NOT_EQUALS: "≠",
  GREATER_THAN: ">",
  LESS_THAN: "<",
  GREATER_THAN_OR_EQUAL: "≥",
  LESS_THAN_OR_EQUAL: "≤",
  IS_NOT_NULL: "is set",
  IS_NULL: "is not set",
};

const COMPARATOR_LABELS: {
  string: Record<StringComparator, string>;
  numeric: Record<NumericComparator, string>;
  date: Record<DateComparator, string>;
  boolean: Record<BooleanComparator, string>;
  set: Record<SetComparator, string>;
  enum: Record<EnumComparator, string>;
  identity: Record<IdentityComparator, string>;
  metric: Record<NumericComparator, string>;
} = {
  string: {
    EQUALS: "equals",
    NOT_EQUALS: "not equals",
    CONTAINS: "contains",
    NOT_CONTAINS: "not contains",
    LIKE: "like",
    NOT_LIKE: "not like",
    IS_NOT_NULL: "is set",
    IS_NULL: "is not set",
  },
  numeric: numericLabels,
  metric: numericLabels,
  date: {
    LESS_THAN: "before",
    GREATER_THAN: "after",
    BETWEEN: "between",
    TODAY: "today",
    LAST_7_DAYS: "last 7 days",
    LAST_30_DAYS: "last 30 days",
    LAST_90_DAYS: "last 90 days",
    THIS_MONTH: "this month",
    IS_NOT_NULL: "is set",
    IS_NULL: "is not set",
  },
  boolean: {
    EQUALS: "equals",
    IS_NOT_NULL: "is set",
    IS_NULL: "is not set",
  },
  set: {
    CONTAINS: "contains",
    NOT_CONTAINS: "not contains",
  },
  enum: {
    EQUALS: "is",
    NOT_EQUALS: "is not",
    IS_NOT_NULL: "is set",
    IS_NULL: "is not set",
  },
  // An identity pill names the principal type it selects — "User" picks
  // specific users, "Any user" matches all of them. Chips read differently (see
  // `comparatorLabelForFilter`): there the pill's meaning is folded into the
  // sentence, as "Created By is Alice" / "Created By is any user".
  identity: {
    IS_USER: IDENTITY_TYPE_LABELS.user,
    IS_DEVICE: IDENTITY_TYPE_LABELS.device,
    IS_INVOCATION: IDENTITY_TYPE_LABELS.invocation,
    IS_INTEGRATION: IDENTITY_TYPE_LABELS.integration,
    IS_ORG: IDENTITY_TYPE_LABELS.org,
    IS_ANY_USER: `Any ${IDENTITY_TYPE_LABELS.user.toLowerCase()}`,
    IS_ANY_DEVICE: `Any ${IDENTITY_TYPE_LABELS.device.toLowerCase()}`,
    IS_ANY_INVOCATION: `Any ${IDENTITY_TYPE_LABELS.invocation.toLowerCase()}`,
    IS_ANY_INTEGRATION: `Any ${IDENTITY_TYPE_LABELS.integration.toLowerCase()}`,
    IS_ANY_ORG: `Any ${IDENTITY_TYPE_LABELS.org.toLowerCase()}`,
  },
};

export function comparatorLabel(
  type: FilterDataType,
  comparator: FilterComparator,
): string {
  // The map is exhaustive per type at the type level, but the public
  // signature accepts the umbrella `FilterComparator` so the
  // OperatorPicker can pass through without narrowing. The cast at the
  // lookup site is safe because callers iterate `comparatorsByType[type]`
  // / `presetsByType[type]`, which are guaranteed-valid for the type.
  return (COMPARATOR_LABELS[type] as Record<string, string>)[comparator];
}

/**
 * Comparator label for an active-filter chip, accounting for cardinality.
 * A multi-value `enum` selection fans out to OR (`EQUALS`) or AND
 * (`NOT_EQUALS`) in `filterToCondition`, so the chip reads "is any of" /
 * "is none of" rather than the singular "is" / "is not".
 *
 * `identity` diverges further: its pills name the principal TYPE ("User", "Any
 * device"), which reads as a sentence fragment on a chip. So the type is folded
 * into the chip's verb instead — a specific pick reads "Created By is Alice"
 * (the value segment carries the name), and the valueless whole-type match
 * reads "Created By is any device" (there is no value segment to carry it).
 *
 * Display-only: the wire `comparator` is unchanged, and the OperatorPicker
 * pills keep their own labels (the picker commits a comparator, not a value
 * count, so it can't know the cardinality yet).
 */
export function comparatorLabelForFilter(filter: Filter): string {
  if (filter.type === "enum" && filter.values.length > 1) {
    if (filter.comparator === "EQUALS") {
      return "is any of";
    }
    if (filter.comparator === "NOT_EQUALS") {
      return "is none of";
    }
  }
  if (filter.type === "identity") {
    const operator = identityOperator(filter.comparator);
    if (operator?.matchesAnyOfType === true) {
      return `is ${anyIdentityTypeLabel(operator.type)}`;
    }
    // A multi-select fans out to OR of EQUALS just like enum, so several picked
    // creators read "is any of Alice, Bob".
    return filter.values.length > 1 ? "is any of" : "is";
  }
  return comparatorLabel(filter.type, filter.comparator);
}

/**
 * Per-(type, comparator) spoken-label overrides. Most types' visual
 * labels are already phrases that read fine — only date diverges
 * (visual "before" for the `LESS_THAN` wire comparator, etc.). Numeric
 * is intentionally absent here: its glyph visuals can't be spoken, so
 * the fallback to the underscored comparator key ("LESS_THAN" →
 * "less than") is exactly right.
 */
const SPOKEN_LABEL_OVERRIDES: Partial<
  Record<FilterDataType, Partial<Record<FilterComparator, string>>>
> = {
  date: {
    LESS_THAN: "before",
    GREATER_THAN: "after",
  },
};

/**
 * Screen-reader-spoken label for a comparator within a field type.
 * Numeric pills render as glyphs ("=", "≠", "≤", …) which screen
 * readers narrate inconsistently across vendors — providing an explicit
 * phrase form gets us deterministic announcement. Date pills display
 * words ("before", "after") that don't match the wire comparator key,
 * so we override there too. Falls back to the comparator key with
 * underscores swapped for spaces ("GREATER_THAN_OR_EQUAL" → "greater
 * than or equal").
 */
export function spokenComparatorLabel(
  type: FilterDataType,
  comparator: FilterComparator,
): string {
  return (
    SPOKEN_LABEL_OVERRIDES[type]?.[comparator] ??
    comparator.toLowerCase().replace(/_/g, " ")
  );
}
